import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type AdminRange = { from: Date; to: Date };

const DAY = 86_400_000;

export function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Inclusive whole-day count of the range. */
export function rangeDays(range: AdminRange) {
  return Math.max(1, Math.round((startOfDay(range.to).getTime() - startOfDay(range.from).getTime()) / DAY) + 1);
}

export function lastNDays(n: number): AdminRange {
  return { from: startOfDay(new Date(Date.now() - (n - 1) * DAY)), to: endOfDay(new Date()) };
}

export function formatRange(range: AdminRange) {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  return `${range.from.toLocaleDateString("en-US", opts)} – ${range.to.toLocaleDateString("en-US", opts)}`;
}

const PRESETS: { label: string; days: number }[] = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "365 days", days: 365 },
];

export function DateRangePicker({
  value,
  onChange,
  className,
}: {
  value: AdminRange;
  onChange: (range: AdminRange) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>({ from: value.from, to: value.to });

  const applyPreset = (days: number) => {
    const next = lastNDays(days);
    setDraft({ from: next.from, to: next.to });
    onChange(next);
    setOpen(false);
  };

  const activePreset = PRESETS.find((p) => {
    const candidate = lastNDays(p.days);
    return (
      startOfDay(candidate.from).getTime() === startOfDay(value.from).getTime() &&
      startOfDay(candidate.to).getTime() === startOfDay(value.to).getTime()
    );
  });

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Quick date ranges">
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => applyPreset(p.days)}
            className={
              activePreset?.days === p.days
                ? "rounded-full border border-[var(--color-ember)] bg-[var(--color-ember)] px-3 py-1 text-xs font-semibold text-[var(--color-ember-foreground)]"
                : "rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="Choose a custom date range"
            className={cn(
              "h-auto rounded-full px-3 py-1 text-xs font-medium",
              !activePreset && "border-[var(--color-ember)] text-[var(--color-ember)]",
            )}
          >
            <CalendarIcon size={14} aria-hidden />
            {activePreset ? "Custom range" : formatRange(value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            defaultMonth={value.from}
            selected={draft}
            onSelect={(next) => {
              setDraft(next);
              if (next?.from && next?.to) {
                onChange({ from: startOfDay(next.from), to: endOfDay(next.to) });
                setOpen(false);
              }
            }}
            disabled={{ after: new Date() }}
            numberOfMonths={2}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
